import {
  contentChildren,
  Directive,
  effect,
  inject,
  input,
  signal,
  untracked
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RawParams, RouterBaseService, StateObject, TransitionOptions } from '../../services/router/router-public-api';

import { TabStateNameDirective } from './tabs-state-name.directive';
import { TabsComponent } from './tabs.component';

import type { InputSignal, Signal, WritableSignal, OnDestroy } from '@angular/core';
import type { TabAsyncSelectEvent } from './tabs.domain';

/**
 * tdWbTabsNavigation directive can be used in conjunction with td-wb-tabs component
 * to navigate to ui router states using tabs and for selecting tab based on the state name
 *
 * This is the parent component to the [Tab Component]{@link TabComponent}.
 */
@Directive({
  selector: 'td-wb-tabs[tdWbTabsNavigation]',
  host: {
    '[attr.role]': 'role()'
  }
})
export class TabsNavigationDirective<T> implements OnDestroy {

  private static readonly TABS_INITIATED_PARAM: string = '$initiatedByTabs';

  readonly paramFilter: InputSignal<(params: RawParams) => RawParams> = input<(params: RawParams) => RawParams>(undefined, { alias: 'tdWbTabsNavigationParamFilter' });

  readonly tabs: Signal<ReadonlyArray<TabStateNameDirective<T>>> = contentChildren(TabStateNameDirective);

  readonly role: WritableSignal<string> = signal('navigation');

  /**
   * This is used to track the state names of the tabs that have been added.
   *
   * @type {Set<string>} A set of strings set to empty on initialization.
   */
  private readonly stateNames: Set<string> = new Set<string>();

  /**
   * This object contains a function called to which is used by the transition service
   * to check if the state names match when using states to control the tabs.
   *
   * @type {unknown}
   */
  private readonly stateHookMatchCriteria: unknown = {
    to: (state: StateObject): boolean => this.stateNames.has(state.name)
        || Array.from(this.stateNames.values()).some((stateName: string): boolean => state.name.startsWith(stateName))
  };

  private readonly destroy: Subject<void> = new Subject<void>();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  private unregisterSuccessHook: Function;

  private readonly routerService: RouterBaseService = inject(RouterBaseService);
  private readonly tabsComponent: TabsComponent<T> = inject(TabsComponent<T>, { host: true });

  constructor() {
    this.tabsComponent.asyncSelect.set(true);

    effect(() => {
      const tabs = this.tabs();
      untracked(() => {
        this.onTabListUpdate(tabs);
        this.setSelectedTabByState();
      });
    });

    this.setSelectedTabByState();
    this.unregisterSuccessHook = this.routerService.transitionOnSuccess(this.stateHookMatchCriteria, (options: TransitionOptions): void => {
      if (options === undefined
        || options.custom === undefined
        || options.custom[TabsNavigationDirective.TABS_INITIATED_PARAM] === undefined) {
        this.setSelectedTabByState();
      }
    });

    outputToObservable(this.tabsComponent.onAsyncSelect).pipe(takeUntil(this.destroy)).subscribe((event: TabAsyncSelectEvent<T>): void => {
      this.navigate(event);
    });
  }

  ngOnDestroy(): void {
    if (this.unregisterSuccessHook !== undefined) {
      this.unregisterSuccessHook();
    }
    this.destroy.next();
    this.destroy.complete();
  }

  /**
   * Updates state names when tab list changes
   */
  private onTabListUpdate(tabs: ReadonlyArray<TabStateNameDirective<T>>): void {
    this.stateNames.clear();
    tabs.forEach((tab: TabStateNameDirective<T>): void => {
      this.stateNames.add(tab.getStateName());
    });
  }

  /**
   * Delegates to [selectTab]{@link TabsComponent#selectTab}
   */
  private setSelectedTabByState(): void {
    const tabComponent = this.findTabByState();
    if (tabComponent !== undefined) {
      Promise.resolve().then((): void => { // "Expression has changed after it was checked" if not async run
        this.tabsComponent.selectTab(tabComponent.getTabId());
      });
    }
  }

  private navigate(event: TabAsyncSelectEvent<T>): void {
    const tabToSelect = this.findTabById(event.tabId);

    if (tabToSelect !== undefined) {
      const stateName = tabToSelect.getStateName();
      if (stateName !== undefined) {
        const promise = this.goToState(stateName, tabToSelect.getStateParams());
        if (promise !== undefined) {
          promise.then((): void => {
            event.select();
          });
          return;
        }
      }
    }
    event.select();
  }

  /**
   * This function is used to find a tab based on the current state.
   *
   * @returns The tab it found.
   * Remarks: as the tabs are defined by states but not parameters, we can use just state name to find it
   */
  private findTabByState(): TabStateNameDirective<T> | undefined {
    return this.tabs()
      .find((tab: TabStateNameDirective<T>): boolean => this.isCurrentState(tab.getStateName(), this.getFilteredStateParams(tab)));
  }

  private getFilteredStateParams(tab: TabStateNameDirective<T>): RawParams {
    return this.paramFilter() === undefined ? undefined : this.paramFilter()(tab.getStateParams());
  }

  private findTabById(tabId: T): TabStateNameDirective<T> | undefined {
    return this.tabs().find((tab: TabStateNameDirective<T>): boolean => tab.getTabId() === tabId);
  }

  /**
   * This function is used to check if the provided state is the current state
   *
   * @param stateName The provided state name to check against.
   * @param [stateParams] The optional state params to check against.
   * @returns Boolean indicating if it is the current state or not.
   */
  private isCurrentState(stateName: string, stateParams?: RawParams): boolean {
    return this.routerService.includes(stateName, stateParams);
  }

  /**
   * This function is used to navigate to the state provided by calling stateService.go,
   * provided there is a state name and it is not the current state.
   *
   * @param stateName The name of the state to go to.
   * @param [stateParams] Optional state params.
   * @param [transitionOptions] Optional state params.
   * @returns The return value from stateService.go.
   */
  private goToState(stateName: string, stateParams?: RawParams, transitionOptions?: TransitionOptions): Promise<unknown> {
    if (stateName !== undefined && !this.isCurrentState(stateName, stateParams)) {
      const options = { ...transitionOptions };
      if (options.custom === undefined) {
        options.custom = {};
      }
      options.custom[TabsNavigationDirective.TABS_INITIATED_PARAM] = true;
      return this.routerService.go(stateName, stateParams, options);
    }
    return undefined;
  }

}
